import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '../../../components/auth/AuthProvider';
import {
  WeatherPreferences,
  WeatherPreferencesStorage,
} from '../services/weatherPreferencesStorage';
import Logger from '../../../utils/logger';

const SYNC_DEBOUNCE_MS = 2000; // Wait 2 seconds before syncing to backend
const RETRY_INTERVAL_MS = 30000; // Retry failed syncs every 30 seconds

export function useWeatherPreferencesSync() {
  const [isSyncing, setIsSyncing] = useState(false);
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasPendingSyncRef = useRef(false); // Track if we have a pending debounced sync
  const lastRetryCheckRef = useRef(0); // Track last retry check to prevent spam

  /**
   * Sync preferences to backend immediately
   */
  const syncToBackend = useCallback(
    async (preferences: WeatherPreferences): Promise<boolean> => {
      if (isSyncing) return false; // Prevent concurrent syncs

      setIsSyncing(true);
      try {
        const { error } = await supabase.auth.updateUser({
          data: {
            custom_model_colors: preferences.customModelColors,
            favorite_weather_variables: preferences.favoriteWeatherVariables,
            favorite_weather_models: preferences.favoriteWeatherModels,
            favorite_cities: preferences.favoriteCities,
          },
        });

        if (error) {
          throw error;
        }

        // Sync successful - clear pending changes flag
        WeatherPreferencesStorage.markSyncSuccessful();
        Logger.info('Successfully synced weather preferences to backend');
        return true;
      } catch (err) {
        Logger.error('Failed to sync weather preferences to backend', err);
        // Keep pending changes flag so we retry later
        return false;
      } finally {
        setIsSyncing(false);
      }
    },
    [isSyncing],
  );

  /**
   * Sync preferences to backend with debouncing
   */
  const debouncedSync = useCallback(
    (preferences: WeatherPreferences) => {
      // Clear existing timeout
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        Logger.info('Cleared existing debounced sync timeout');
      }

      // Mark that we have a pending sync
      hasPendingSyncRef.current = true;
      Logger.info('Scheduling debounced sync in 2 seconds...');

      // Set new timeout
      syncTimeoutRef.current = setTimeout(async () => {
        hasPendingSyncRef.current = false; // Clear pending flag before sync
        Logger.info('Executing debounced sync');
        await syncToBackend(preferences);
      }, SYNC_DEBOUNCE_MS);
    },
    [syncToBackend],
  );

  /**
   * Check for failed syncs and retry if needed
   */
  const checkAndRetryFailedSync = useCallback(() => {
    const now = Date.now();

    // Cooldown: Don't check more than once every 5 seconds
    if (now - lastRetryCheckRef.current < 5000) {
      return;
    }
    lastRetryCheckRef.current = now;

    const hasPendingChanges = WeatherPreferencesStorage.hasPendingChanges();
    const lastSuccessfulSync = WeatherPreferencesStorage.getLastSuccessfulSync();

    Logger.info('Retry check:', {
      hasPendingChanges,
      isSyncing,
      hasPendingSync: hasPendingSyncRef.current,
      timeSinceLastSync: now - lastSuccessfulSync,
    });

    // Don't retry if we have a pending debounced sync or are currently syncing
    if (hasPendingChanges && !isSyncing && !hasPendingSyncRef.current) {
      const timeSinceLastSync = now - lastSuccessfulSync;

      if (timeSinceLastSync > RETRY_INTERVAL_MS) {
        // Retry the sync
        const preferences = WeatherPreferencesStorage.getPreferences();
        if (preferences) {
          Logger.info('Retrying failed sync...');
          syncToBackend(preferences);
        }
      }
    }
  }, [isSyncing, syncToBackend]);

  /**
   * Fetch preferences from backend
   */
  const fetchFromBackend = useCallback(async (): Promise<WeatherPreferences> => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      return {
        favoriteWeatherVariables: user?.user_metadata?.favorite_weather_variables || [],
        favoriteWeatherModels: user?.user_metadata?.favorite_weather_models || {},
        favoriteCities: user?.user_metadata?.favorite_cities || {},
        customModelColors: user?.user_metadata?.custom_model_colors || {},
      };
    } catch (err) {
      Logger.error('Failed to fetch preferences from backend', err);
      return {
        favoriteWeatherVariables: [],
        favoriteWeatherModels: {},
        favoriteCities: {},
        customModelColors: {},
      };
    }
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        hasPendingSyncRef.current = false; // Clear pending flag on cleanup
      }
    };
  }, []);

  return {
    isSyncing,
    syncToBackend,
    debouncedSync,
    checkAndRetryFailedSync,
    fetchFromBackend,
  };
}
